import { Box, Button, FormHelperText, Stack, styled, Typography } from '@mui/material';
import { type ReactNode, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { WorkflowStepFieldInput } from 'src/pages/Settings/workflows/components/fields/workflow-step-field-input';
import { WorkflowNodeType } from 'src/pages/Settings/workflows/utils/workflow-definition';
import { countStepsDroppedWithIf } from 'src/pages/Settings/workflows/utils/workflow-definition-edits';
import { formatNodeTitle } from 'src/pages/Settings/workflows/utils/workflow-helpers';
import { useWorkflowEditor } from 'src/pages/Settings/workflows/workflow-editor-context';
import { useNotifications } from 'src/utils/hooks/use-notifications';

const RawData = styled('pre')(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainer,
  fontSize: '0.75rem',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));

const ConfirmBox = styled(Stack)(({ theme }) => ({
  border: `1px solid ${theme.palette.error.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  gap: theme.spacing(1),
}));

const stepsText = (count: number) => `${count} ${count === 1 ? 'step' : 'steps'}`;

/**
 * The open card's body: every catalog field for the step, then what the step is — its type and id,
 * whatever it was renamed to — and Delete. Deleting an
 * `if` first asks which output to keep and how many steps go with the other. The trigger has no
 * Delete. An unknown step shows its stored data, read-only.
 */
export const WorkflowStepForm = ({ nodeId }: { nodeId: string }) => {
  const { definition, catalog, problemsByNode, editable, dispatch, setOpenNodeId } =
    useWorkflowEditor();
  const [confirmingIfDelete, setConfirmingIfDelete] = useState(false);
  const { showInfoAlert } = useNotifications();

  const node = definition.nodes.find(({ id }) => id === nodeId);
  if (!node) return null;

  const stepType = catalog.byType.get(node.type);
  const problems = problemsByNode.get(nodeId) ?? [];
  const stepProblems = problems.filter(({ field }) => !field);
  const isTrigger = node.type === WorkflowNodeType.Trigger;
  // the trigger's settings are chosen by the trigger picker (1V-6177), not here
  const readOnly = !editable || isTrigger;

  const deleteStep = (keepCondition?: boolean) => {
    dispatch({
      kind: 'deleteStep',
      nodeId,
      ...(keepCondition === undefined ? {} : { keepCondition }),
    });
    setOpenNodeId(null);
    showInfoAlert('Step removed');
  };

  const outputs = stepType?.outputs ?? [];
  const otherLabel = (condition: boolean | null | undefined) =>
    outputs.find((output) => output.condition === !condition)?.label ?? '';

  let fieldsContent: ReactNode;
  if (!stepType) {
    fieldsContent = (
      <Stack spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          {editable
            ? 'This step type isn’t supported any more, so a draft with it can’t be saved. Delete it to save your changes.'
            : 'This step type isn’t supported any more. It’s shown here as it is stored.'}
        </Typography>
        <RawData>{JSON.stringify(node.data ?? {}, null, 2)}</RawData>
      </Stack>
    );
  } else if (!stepType.fields.length) {
    fieldsContent = (
      <Typography variant="body2" color="text.secondary">
        {stepType.description}
      </Typography>
    );
  } else {
    fieldsContent = stepType.fields.map((field) => (
      <WorkflowStepFieldInput
        key={field.key}
        nodeId={nodeId}
        field={field}
        path={field.key}
        data={node.data}
        problems={problems}
        readOnly={readOnly}
        onChange={(fieldPath, value) =>
          dispatch({ kind: 'updateField', nodeId, fieldPath, value })
        }
      />
    ));
  }

  return (
    <Stack spacing={1.5} sx={{ mt: 1.5 }}>
      {fieldsContent}

      {stepProblems.map((problem) => (
        <FormHelperText key={problem.message} error sx={{ mx: 0 }}>
          {problem.message}
        </FormHelperText>
      ))}

      {confirmingIfDelete ? (
        <ConfirmBox>
          <Typography variant="body2">Delete this If and keep which path?</Typography>
          {outputs.map((output) => (
            <Typography key={output.key} variant="caption" color="text.secondary">
              Keeping {output.label} removes{' '}
              {stepsText(
                countStepsDroppedWithIf(definition, nodeId, output.condition === true),
              )}{' '}
              on the {otherLabel(output.condition)} path.
            </Typography>
          ))}
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {outputs.map((output) => (
              <Button
                key={output.key}
                size="small"
                color="error"
                variant="outlined"
                onClick={() => deleteStep(output.condition === true)}
              >
                Keep {output.label}
              </Button>
            ))}
            <Button size="small" onClick={() => setConfirmingIfDelete(false)}>
              Cancel
            </Button>
          </Stack>
        </ConfirmBox>
      ) : null}

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}
      >
        <Box flex={1} minWidth={0}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}
          >
            {formatNodeTitle(node.type, catalog)} · {nodeId}
          </Typography>
        </Box>
        {editable && !isTrigger && !confirmingIfDelete ? (
          <Button
            size="small"
            color="error"
            startIcon={<DynamicIcon name="delete" fontSize="small" />}
            onClick={() =>
              node.type === WorkflowNodeType.If ? setConfirmingIfDelete(true) : deleteStep()
            }
          >
            Delete
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
};
